import { Modal, Spinner } from "react-bootstrap";
import { CloseCircle, Dislike, Eye, Like1, Paperclip2, Send2 } from "iconsax-react";
import moment from "moment-jalaali";
import { get, post, del } from "../../../controlers/global/ConfigRequest";
import {
    CLUB_DISLIKE_CONTENT,
    CLUB_LIKE_CONTENT,
    CLUB_SEND_COMMENT,
    GET_CLUB_COMMENT,
    GET_CLUB_CONTENT,
} from "../../../helperData/ApiAddress";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import MediaPreview from "../../../features/content-studio/MediaPreview";
import { CONTENT_TYPE } from "../../../features/content-studio/constants";
import { formatCount } from "../../../features/content-studio/helpers";
import "../../../features/content-studio/styles.scss";
import FidimaBrand from "./FidimaBrand";

moment.loadPersian();

const commentRows = (payload) =>
    Array.isArray(payload) ? payload : payload?.results || [];

const nextCounts = (content, nextVote) => {
    const likes = Number(content.likes_count || 0);
    const dislikes = Number(content.dislikes_count || 0);
    const current = content.my_vote;
    if (current === nextVote) {
        return {
            likes_count: nextVote === "like" ? Math.max(0, likes - 1) : likes,
            dislikes_count:
                nextVote === "dislike" ? Math.max(0, dislikes - 1) : dislikes,
            my_vote: null,
        };
    }
    return {
        likes_count:
            nextVote === "like"
                ? likes + 1
                : current === "like"
                ? Math.max(0, likes - 1)
                : likes,
        dislikes_count:
            nextVote === "dislike"
                ? dislikes + 1
                : current === "dislike"
                ? Math.max(0, dislikes - 1)
                : dislikes,
        my_vote: nextVote,
    };
};

const commenterName = (user, fallback) => {
    if (!user) return fallback;
    const full = `${user.first_name || ""} ${user.last_name || ""}`.trim();
    return full || user.username || fallback;
};

const hasPlayableMedia = (content) => {
    if (!content) return false;
    if (content.file || content.embed_url || content.thumbnail_url) return true;
    if (content.url && Number(content.content_type) !== CONTENT_TYPE.TEXT) {
        return true;
    }
    return false;
};

export default function UserContentModal({ show, onHide }) {
    const [content, setContent] = useState();
    const [comments, setComments] = useState([]);
    const [loader, setLoader] = useState(true);
    const [voteBusy, setVoteBusy] = useState(false);
    const [commentBody, setCommentBody] = useState("");
    const [fileBase64, setFileBase64] = useState("");
    const [fileName, setFileName] = useState("");
    const [commentResult, setCommentResult] = useState(["", 0]);
    const [commentSendLoader, setCommentSendLoader] = useState(false);
    const [commentListLoader, setCommentListLoader] = useState(false);
    const { t, i18n } = useTranslation();
    const locale = i18n.language === "fa" ? "fa" : "en";

    const getModal = () => {
        setLoader(true);
        Promise.all([
            get(GET_CLUB_CONTENT.replace(":id", show)),
            get(GET_CLUB_COMMENT.replace(":id", show)),
        ])
            .then((r) => {
                setContent(r[0]);
                setComments(commentRows(r[1]));
            })
            .finally(() => {
                setLoader(false);
            });
    };

    const getComment = () => {
        setCommentListLoader(true);
        get(GET_CLUB_COMMENT.replace(":id", show))
            .then((r) => {
                setComments(commentRows(r));
            })
            .finally(() => {
                setCommentListLoader(false);
            });
    };

    useEffect(() => {
        if (show > 0) getModal();
    }, [show]);

    const vote = (nextVote) => {
        if (!content || voteBusy) return;
        const previous = {
            likes_count: content.likes_count,
            dislikes_count: content.dislikes_count,
            my_vote: content.my_vote,
        };
        setContent({ ...content, ...nextCounts(content, nextVote) });
        setVoteBusy(true);
        const request =
            content.my_vote === nextVote
                ? del(CLUB_LIKE_CONTENT.replace(":id", show))
                : nextVote === "like"
                ? post(CLUB_LIKE_CONTENT.replace(":id", show))
                : post(CLUB_DISLIKE_CONTENT.replace(":id", show));
        request
            .catch(() => {
                setContent((current) => ({ ...current, ...previous }));
            })
            .finally(() => {
                setVoteBusy(false);
            });
    };

    const toBase64 = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });

    const fileHandle = (e) => {
        const file = e.target.files[0];
        setCommentResult(["", 0]);
        if (!file) return;
        if (file.size > 2097152) {
            setCommentResult([
                t("Src.pages.UserPages.CustomerClub.UserContentClub.Size.Err"),
                0,
            ]);
            return;
        }
        setFileName(file.name);
        toBase64(file).then((result) => {
            setFileBase64(result);
        });
    };

    const sendComment = () => {
        setCommentResult(["", 0]);
        if (commentBody.trim().length === 0) {
            setCommentResult([
                t(
                    "Src.pages.UserPages.CustomerClub.UserContentClub.Comment.Err"
                ),
                0,
            ]);
            return;
        }

        setCommentSendLoader(true);
        post(CLUB_SEND_COMMENT, {
            comment: commentBody.trim(),
            file: fileBase64 || undefined,
            content: show,
        })
            .then(() => {
                setCommentResult([
                    t(
                        "Src.pages.UserPages.CustomerClub.UserContentClub.Comment.Success"
                    ),
                    1,
                ]);
                setCommentBody("");
                setFileBase64("");
                setFileName("");
                getComment();
            })
            .finally(() => {
                setCommentSendLoader(false);
            });
    };

    const companyName =
        content?.company_name || content?.company?.name || "";
    const showMedia = hasPlayableMedia(content);
    const isAudio = Number(content?.content_type) === CONTENT_TYPE.AUDIO;

    const viewer =
        loader || !content ? (
            <div className="share-viewer">
                <header className="share-viewer__brand">
                    <FidimaBrand compact />
                </header>
                <div className="share-viewer__loader">
                    <Spinner animation="border" />
                </div>
            </div>
        ) : (
            <div className="share-viewer">
                <header className="share-viewer__brand">
                    <FidimaBrand compact />
                </header>
                {onHide ? (
                    <button
                        type="button"
                        className="share-viewer__close"
                        onClick={onHide}
                        aria-label={t(
                            "Src.pages.UserPages.CustomerClub.UserContentClub.Close"
                        )}
                    >
                        <CloseCircle size={22} />
                    </button>
                ) : null}
                {showMedia ? (
                    <div
                        className={
                            "share-viewer__stage" +
                            (isAudio ? " is-audio" : "")
                        }
                    >
                        <MediaPreview content={content} />
                    </div>
                ) : null}
                <div className="share-viewer__body">
                    {companyName ? (
                        <p className="share-viewer__eyebrow">
                            {companyName}
                        </p>
                    ) : null}
                    <h2>{content.name}</h2>
                    {content.description ? (
                        <p className="share-viewer__copy">
                            {content.description}
                        </p>
                    ) : null}
                    <div className="share-viewer__meta">
                        {content.created_at || content.date ? (
                            <time>
                                {moment(
                                    content.created_at || content.date
                                ).format("jYYYY/jMM/jDD")}
                            </time>
                        ) : null}
                        <span>
                            <Eye size={16} aria-hidden="true" />
                            {formatCount(content.views_count, locale)}
                            <span>
                                {t(
                                    "Src.pages.UserPages.CustomerClub.UserContentClub.Views"
                                )}
                            </span>
                        </span>
                    </div>
                    <div className="share-viewer__react" role="group">
                        <button
                            type="button"
                            className={
                                content.my_vote === "like" ? "is-on" : ""
                            }
                            aria-pressed={content.my_vote === "like"}
                            aria-label={t("contents.like")}
                            disabled={voteBusy}
                            onClick={() => vote("like")}
                        >
                            <Like1
                                size={20}
                                variant={
                                    content.my_vote === "like"
                                        ? "Bold"
                                        : "Linear"
                                }
                            />
                            <span>
                                {formatCount(content.likes_count, locale)}
                            </span>
                        </button>
                        <button
                            type="button"
                            className={
                                content.my_vote === "dislike"
                                    ? "is-on is-down"
                                    : ""
                            }
                            aria-pressed={content.my_vote === "dislike"}
                            aria-label={t("contents.dislike")}
                            disabled={voteBusy}
                            onClick={() => vote("dislike")}
                        >
                            <Dislike
                                size={20}
                                variant={
                                    content.my_vote === "dislike"
                                        ? "Bold"
                                        : "Linear"
                                }
                            />
                            <span>
                                {formatCount(
                                    content.dislikes_count,
                                    locale
                                )}
                            </span>
                        </button>
                    </div>
                    <section className="share-viewer__comments">
                        <h3>
                            {t(
                                "Src.pages.UserPages.CustomerClub.UserContentClub.User.Comment"
                            )}
                            {comments.length ? (
                                <span>{comments.length}</span>
                            ) : null}
                        </h3>
                        {commentListLoader ? (
                            <div className="share-viewer__loader share-viewer__loader--inline">
                                <Spinner animation="border" size="sm" />
                            </div>
                        ) : comments.length === 0 ? (
                            <p className="share-viewer__empty">
                                {t(
                                    "Src.pages.UserPages.CustomerClub.UserContentClub.User.Comment.Not.Found"
                                )}
                            </p>
                        ) : (
                            <ul>
                                {comments.map((row) => (
                                    <li key={row.id}>
                                        <div>
                                            <strong>
                                                {commenterName(
                                                    row.user,
                                                    t(
                                                        "Src.pages.UserPages.CustomerClub.UserContentClub.Anonymous"
                                                    )
                                                )}
                                            </strong>
                                            <time>
                                                {moment(
                                                    row.datetime
                                                ).fromNow()}
                                            </time>
                                        </div>
                                        {row.comment ? (
                                            <p>{row.comment}</p>
                                        ) : null}
                                        {row.file ? (
                                            <a
                                                href={row.file}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                {t(
                                                    "Src.pages.UserPages.CustomerClub.UserContentClub.Show.Image"
                                                )}
                                            </a>
                                        ) : null}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                </div>
                <form
                    className="share-viewer__composer"
                    onSubmit={(event) => {
                        event.preventDefault();
                        sendComment();
                    }}
                >
                    {commentResult[0] ? (
                        <p
                            className={
                                "share-viewer__status " +
                                (commentResult[1] === 0
                                    ? "is-error"
                                    : "is-ok")
                            }
                            role="status"
                        >
                            {commentResult[0]}
                        </p>
                    ) : null}
                    {fileName ? (
                        <p className="share-viewer__file">{fileName}</p>
                    ) : null}
                    <div className="share-viewer__field">
                        <label className="share-viewer__attach">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={fileHandle}
                            />
                            <Paperclip2 size={18} aria-hidden="true" />
                            <span className="sr-only">
                                {t(
                                    "Src.pages.UserPages.CustomerClub.UserContentClub.Comment.Attach"
                                )}
                            </span>
                        </label>
                        <textarea
                            rows={1}
                            value={commentBody}
                            placeholder={t(
                                "Src.pages.UserPages.CustomerClub.UserContentClub.Comment.Placeholder"
                            )}
                            onChange={(e) => setCommentBody(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    sendComment();
                                }
                            }}
                        />
                        <button
                            type="submit"
                            disabled={
                                commentSendLoader || commentBody.trim() === ""
                            }
                            aria-label={t(
                                "Src.pages.UserPages.CustomerClub.UserContentClub.Comment.Send"
                            )}
                        >
                            {commentSendLoader ? (
                                <Spinner animation="border" size="sm" />
                            ) : (
                                <Send2 size={18} />
                            )}
                        </button>
                    </div>
                </form>
            </div>
        );

    if (onHide) {
        return (
            <Modal
                show={Boolean(show)}
                onHide={onHide}
                centered={true}
                scrollable={true}
                fullscreen="sm-down"
                className="club-content-modal"
            >
                <Modal.Body>{viewer}</Modal.Body>
            </Modal>
        );
    }

    return <div className="club-content-page">{viewer}</div>;
}
